// frontend/src/pages/FullDigestViewPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDailyDigest } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ThemeArticles from '../components/Dashboard/ThemeArticles'; // Re-use this
import { ArrowLeftIcon, NewspaperIcon, LightBulbIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const FullDigestViewPage = () => {
  const { date } = useParams(); // Get date from URL
  const [digest, setDigest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (date) {
      const fetchDigest = async () => {
        setIsLoading(true);
        setError('');
        try {
          const data = await getDailyDigest(date);
          if (data && data.id) {
            setDigest(data);
          } else {
            setError('Daily digest not found for this date.');
            setDigest(null);
          }
        } catch (err) {
          setError(err.message || 'Failed to load daily digest.');
          setDigest(null);
        } finally {
          setIsLoading(false);
        }
      };
      fetchDigest();
    } else {
      setError('No date provided for the digest.');
      setIsLoading(false);
    }
  }, [date]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <LoadingSpinner />
        <p className="mt-2 text-gray-600">Loading digest...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-red-500 bg-red-100 p-4 rounded-md">{error}</p>
        <Link to="/daily-digests" className="mt-4 inline-block text-blue-600 hover:underline">
          &larr; Back to Digests
        </Link>
      </div>
    );
  }

  if (!digest) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Digest data is not available.</p>
         <Link to="/daily-digests" className="mt-4 inline-block text-blue-600 hover:underline">
          &larr; Back to Digests
        </Link>
      </div>
    );
  }

  let parsedContent = null;
  if (digest && typeof digest.content === 'string') {
    try {
      parsedContent = JSON.parse(digest.content);
    } catch (e) {
      console.error("[FullDigestViewPage] Failed to parse digest content JSON string:", e);
      setError("Digest content is corrupted.");
      return ( // Return error message if parsing fails
         <div className="container mx-auto px-4 py-8 text-center">
            <p className="text-red-500 bg-red-100 p-4 rounded-md">{error}</p>
             <Link to="/daily-digests" className="mt-4 inline-block text-blue-600 hover:underline">
              &larr; Back to Digests
            </Link>
        </div>
      );
    }
  } else if (digest && typeof digest.content === 'object') {
    parsedContent = digest.content;
  }

  if (!parsedContent || typeof parsedContent !== 'object' || !parsedContent.overall_introduction || !parsedContent.featured_themes) {
     setError("Digest content is incomplete or malformed.");
     return (
         <div className="container mx-auto px-4 py-8 text-center">
            <p className="text-red-500 bg-red-100 p-4 rounded-md">{error}</p>
            <Link to="/daily-digests" className="mt-4 inline-block text-blue-600 hover:underline">
              &larr; Back to Digests
            </Link>
        </div>
      );
  }

  const { overall_introduction, featured_themes } = parsedContent;
  const formattedDate = new Date(digest.date + "T00:00:00Z").toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
  });

  const Breadcrumbs = () => (
    <nav className="flex mb-6" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
        <li className="inline-flex items-center">
          <Link to="/daily-digests" className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
            <NewspaperIcon className="w-4 h-4 mr-2" />
            Digests
          </Link>
        </li>
        <li>
          <div className="flex items-center">
            <ChevronRightIcon className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" />
            <span className="ms-1 text-sm font-medium text-gray-500 md:ms-2">
              {formattedDate}
            </span>
          </div>
        </li>
      </ol>
    </nav>
  );

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs />

      <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200/80">
        <div className="bg-blue-600 p-6 sm:p-8">
          <div className="flex items-center">
            <NewspaperIcon className="h-8 w-8 sm:h-9 sm:w-9 mr-3 sm:mr-3.5 text-blue-100 flex-shrink-0" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">{digest.title}</h1>
          </div>
           <p className="text-base text-blue-200 mt-2">For {formattedDate}</p>
        </div>

        <div className="p-6 sm:p-8 space-y-10">
          {overall_introduction && (
            <section className="p-6 bg-slate-50 rounded-lg">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3">Overview</h2>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">{overall_introduction}</p>
            </section>
          )}

          {featured_themes && featured_themes.length > 0 && (
            <section className="space-y-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">Featured Themes</h2>
              {featured_themes.map((theme, index) => (
                <section key={index} className="p-6 bg-white rounded-lg shadow-md border border-gray-200/60 last:border-b-0">
                  <div className="flex items-center mb-3">
                    <LightBulbIcon className="h-7 w-7 text-blue-500 mr-2.5 flex-shrink-0" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800">{theme.theme_title || "Untitled Theme"}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed whitespace-pre-wrap">{theme.theme_description || "No description for this theme."}</p>
                  
                  {theme.article_ids && theme.article_ids.length > 0 ? (
                    <div className="mt-4">
                      <h4 className="text-md font-semibold text-gray-700 mb-3">
                        Relevant Articles:
                      </h4>
                      <ThemeArticles articleIds={theme.article_ids} themeTitle={theme.theme_title} />
                    </div>
                  ) : (
                     <p className="text-sm text-gray-500 italic mt-2">
                       No specific articles were highlighted for this theme in the digest.
                     </p>
                   )}
                </section>
              ))}
            </section>
          )}
          
          <p className="text-xs text-gray-500 mt-12 pt-6 border-t border-gray-200 text-right">
            Digest for: {formattedDate}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FullDigestViewPage;
